/**
 * `/o/:org/reviews/:draftId` - the human review, and the centre of this product.
 *
 * V1 section 9 requires this screen to show, before/after preview, a source diff
 * option, changed files, the reason, a checklist, reviewer state and publication
 * progress, and it requires it to "show exactly which revision/hash is
 * approved". This file does that.
 *
 * WHAT IS REAL HERE
 *   - the base revision and its files, with content hashes;
 *   - the draft's files and their content hashes;
 *   - the document body of the current release, fetched from the API's bundle
 *     route, which also records an observed retrieval;
 *   - the DRAFT's own document body, fetched through
 *     `GET /v1/drafts/:draftId/files` — the route added when the editor became
 *     able to save real bytes. This file previously claimed that path did not
 *     exist and rendered "After: Not available" in its place. That claim was
 *     true when written and is false now, and a screen that says a thing is
 *     unavailable when it is available is worse than one that never claimed to
 *     know.
 *   - a LINE-LEVEL DIFF between those two bodies, which is what makes the
 *     changed-files list usable: a reviewer needs to know which lines moved,
 *     not only which files.
 *   - the approval that is already stored, with the hash it is bound to;
 *   - a SELF-REVIEW tag on any decision whose reviewer was the draft's author.
 *     This was the gap: `reviews.self_review` records what HAPPENED, while the
 *     organization flag on the admin screen records what was PERMITTED, and
 *     only the latter was rendered anywhere. An auditor reading the flag could
 *     learn that the exception was enabled and could not learn whether anyone
 *     had used it. The two facts are separate and the screen now shows both.
 *   - the checklist, and the decision rules in `review-model.ts`.
 *
 * WHAT IS NOT, AND WHY
 *   - A diff for any file other than `SKILL.md`. `release_files` carries no
 *     content column — the bytes of a published release live in object storage
 *     and the bundle route serves only the packaged manifest document — so for
 *     `references/*` the change set stays a content-hash comparison, which is
 *     what can be proved. The screen says which is which rather than implying
 *     the whole package was read.
 *   - A diff against a base that is not the current release. The bundle route
 *     authorises and serves `skills.current_release_id`, so when a draft was
 *     written against an older revision those are two different documents and
 *     diffing them would show a reviewer changes nobody made. The screen refuses
 *     the comparison in that case and says why.
 *   - Nothing, as a separate screen. V1 section 6 requires a human to approve a specific hash
 *     and then explicitly select Publish. That step lives in `publication.ts` and renders on
 *     this screen as its own panel below the decision — a second act, with its own control,
 *     because approving content and making it current are not the same decision.
 */

import type { AppShell, ScreenState } from '../app.js';
import * as api from '../api/queries.js';
import { type Failure, type Result, ok } from '../api/client.js';
import { type RawHtml, html, raw } from '../lib/html.js';
import { type PeopleDirectory, formatDateTime, personLabel, shortHash } from '../lib/format.js';
import { renderMarkdown } from '../lib/markdown.js';
import {
  type DiffRow,
  type LineDiff,
  diffLines,
  opLabel,
  signOf,
} from '../lib/diff.js';
import {
  REVIEW_CHECKS,
  type ChangedFile,
  type ContentBinding,
  type SelfReviewException,
  changedFiles,
  contentBinding,
  decide,
  mayRequestChanges,
  parseCheckpoints,
} from '../lib/review-model.js';
import { docHead, failurePanel, presentFailure, skeletonRows, tag } from '../ui/components.js';
import { renderPublication } from './publication.js';

/**
 * The one path this review diffs.
 *
 * Stated once, for the same reason `DRAFT_DOCUMENT_PATH` is stated once in the
 * editor: a preview, a diff and an approval that each name the document
 * separately are three chances to review a different file from the one that was
 * approved.
 */
const DOCUMENT_PATH = 'SKILL.md';

export interface ReviewData {
  readonly draft: api.DraftRow;
  readonly skill: api.SkillRow | null;
  readonly baseRelease: api.ReleaseRow | null;
  readonly baseFiles: readonly api.ReleaseFileRow[];
  readonly draftFiles: readonly api.DraftFileRow[];
  readonly reviews: readonly api.ReviewRow[];
  readonly document: string | null;
  /**
   * The DRAFT's own document body, or null.
   *
   * Deliberately a separate field from `document`, which is the PUBLISHED
   * release's. Overloading one name with both is how the previous version of
   * this screen came to render the published text under an "after" heading.
   */
  readonly draftDocument: string | null;
  /** Why the draft body could not be read, when it was attempted and failed. */
  readonly draftDocumentFailure: Failure | null;
  /**
   * Whether the published body may be used as the diff's "before".
   *
   * The bundle route authorises and serves `skills.current_release_id`, so it
   * answers with the CURRENT release. When a draft was written against an older
   * revision those are different documents, and diffing them would show a
   * reviewer a set of changes nobody made — in the one screen whose entire job
   * is to be right about what changed. Null base (a first publication) is
   * current by definition: there is no "before", so nothing can be stale.
   */
  readonly diffBaseIsCurrent: boolean;
  /**
   * The latest publication for this draft, or null when nothing has been
   * published from it.
   *
   * NULL IS NOT AN ERROR, AND IT IS NOT THE SAME AS THE FIELD BELOW.
   * `GET /v1/drafts/:id/publication` answers 404 both for a draft that was
   * never published and for a job this caller may not see, and the two are
   * deliberately indistinguishable (0048's header). `getPublication` therefore
   * folds the 404 into `null` ONLY where the draft's own state proves no job
   * can exist (`queries.ts`'s `NEVER_PUBLISHED_STATES`) — this screen has
   * already read the draft, so it holds that fact rather than asking again.
   *
   * `publicationFailure` carries everything else — a 403, a 500, a dropped
   * connection, an expired session, and the 404 that means a job exists but is
   * hidden from this caller — which is NOT "nothing has been published".
   * Rendering the second as the first is the false-negative twin of the false
   * success UI-02 forbids: a person told nothing is publishing, when in fact
   * nobody can see whether something is. It also does not replace the screen,
   * because the diff, the changed files, the checklist and the decision were
   * all read successfully and are unaffected by one unavailable sub-resource.
   */
  readonly publication: api.PublishJobStatusRow | null;
  readonly publicationFailure: Failure | null;
  /**
   * Whether this organization has enabled the audited self-review exception.
   *
   * READ FRESH, and from the same row the admin screen's control writes:
   * `GET /v1/organizations/current` answers with the organization's own
   * `allow_self_review` column, and `PUT /v1/orgs/:orgId/policy/self-review`
   * writes that same column through `app.set_self_review_policy`
   * (`0061_self_review_policy_rpc.sql`). The screen and the database are
   * therefore asking about one fact and not two.
   *
   * It is read here rather than taken from `app.state.organization` because
   * that value is read ONCE, when the tab signs in, and never refreshed: a
   * tab that enabled the exception and then opened a review would decide on
   * the value from before. The database is the authority and this screen asks
   * it.
   */
  readonly selfReview: SelfReviewException;
}

/**
 * A document body, or null when there is not one to render.
 *
 * WHY THIS IS NOT `value === null`. `ReviewData` reaches this view as
 * `ScreenState`'s `unknown` and is cast, so its declared types are enforced
 * NOWHERE on the way in: an omitted key arrives as `undefined`, not as the
 * `null` the type promises. A `=== null` guard walks straight past
 * `undefined`, and the next thing to touch the value is `.split` or the
 * markdown renderer — so the whole review screen throws.
 *
 * That was not hypothetical. A fixture which simply did not mention a newly
 * added field took ten unrelated tests down with it, and
 * @ResponsiveLayoutFixes reported the stack rather than fixing it in my file.
 * A hand-built payload does not exercise the boundary its own type describes,
 * which is the same shape as a golden string produced by the code under test.
 *
 * `editor.ts` states this boundary in its own `draftBytesOf`/`draftFailureOf`
 * and checks TYPES rather than comparing to null. Same guard, same reason, in
 * the file that needed it second.
 */
function bodyOf(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** The kind of a recorded failure as text, or null when there is none. */
function failureKindOf(value: unknown): string | null {
  const kind = (value as { readonly kind?: unknown } | null)?.kind;
  return typeof kind === 'string' ? kind : null;
}

/**
 * The self-review exception, read defensively from the payload.
 *
 * `ReviewData` reaches this view as `ScreenState`'s `unknown` and is cast, so
 * an omitted key arrives as `undefined` and not as the `null` the type
 * promises — the same boundary `bodyOf` and `failureKindOf` guard above, for
 * the same reason: a fixture that did not mention a newly added field once
 * took ten unrelated tests down with it.
 *
 * AN UNREAD SETTING FAILS CLOSED. The approval is not offered, because the
 * database admits a self-review only when the organization has enabled the
 * exception (`0034_reviewer_separation.sql:80-92`) and this screen cannot tell
 * whether it has. The refusal that follows says the setting could not be
 * read, which is a different sentence from "your organization has not
 * enabled it" — and the only one of the two that is true.
 */
function selfReviewKindOf(value: unknown): SelfReviewException {
  const kind = (value as { readonly kind?: unknown } | null)?.kind;
  if (kind === 'allowed') return { kind: 'allowed' };
  if (kind === 'not-allowed') return { kind: 'not-allowed' };
  return {
    kind: 'unknown',
    reason: 'The organization self-review setting did not reach this screen.',
  };
}

export async function loadReview(
  app: AppShell,
  orgId: string,
  draftId: string,
): Promise<Result<ReviewData>> {
  void orgId;
  const draft = await api.getDraft(app.client, draftId);
  if (!draft.ok) return draft;

  const [files, reviews, skills, organization] = await Promise.all([
    api.listDraftFiles(app.client, draftId),
    api.listReviews(app.client, draftId),
    api.loadSkills(app.client, [draft.value.skill_id]),
    // THE ORGANIZATION'S OWN ROW, read for the one fact the review decision
    // needs from it: `allow_self_review`. The same route the admin screen
    // reads, and the same column its control writes through
    // `app.set_self_review_policy` (`0061_self_review_policy_rpc.sql`).
    api.getOrganization(app.client),
  ]);
  if (!files.ok) return files;
  if (!reviews.ok) return reviews;
  if (!skills.ok) return skills;

  const baseFiles =
    draft.value.base_release_id === null
      ? ok<readonly api.ReleaseFileRow[]>([])
      : await api.listReleaseFiles(app.client, draft.value.base_release_id);
  if (!baseFiles.ok) return baseFiles;

  const releases =
    draft.value.base_release_id === null
      ? ok<readonly api.ReleaseRow[]>([])
      : await api.loadReleases(app.client, [draft.value.base_release_id]);
  if (!releases.ok) return releases;

  // The draft's own body, and it is a separate request from the bundle on
  // purpose: one is the bytes being reviewed and the other is what readers see
  // today. Fetching them together is fine; conflating them is not.
  const stored = await api.readDraftFile(app.client, draftId, DOCUMENT_PATH);

  const skill = skills.value[0] ?? null;
  let document: string | null = null;
  if (draft.value.skill_id !== '' && draft.value.base_release_id !== null) {
    const bundle = await api.downloadBundle(app.client, draft.value.skill_id);
    document = bundle.ok ? bundle.value : null;
  }

  // Publication is read on EVERY load, not only while something is in flight.
  // The worker finishes on its own schedule and usually outlives the tab that
  // asked for it, so the person who reloads — or comes back tomorrow — is
  // exactly the person who needs the outcome. `last_error` in particular is on
  // no other surface, and P3 line 56 asks for the real reason rather than a
  // generic failure.
  // The draft's own state goes with it, because that state is what makes a 404
  // interpretable: it is the difference between "this draft was never published"
  // and "a job exists and you may not see it", and only the second must reach
  // the panel as a failure.
  const publication = await api.getPublication(app.client, draftId, draft.value.state);
  // A failed publication read is NOT a screen failure, and dropping the guard
  // that used to return one is the whole point of `publicationFailure`:
  // everything else this screen renders — the diff, the changed files, the
  // checklist, the decision — was read successfully and is unaffected by one
  // unavailable sub-resource. Returning it would have replaced a reviewer's
  // ability to do their job with "could not load".



  // THE SELF-REVIEW EXCEPTION, as the organization has it.
  //
  // A FAILED READ IS NOT "THE ORGANIZATION HAS NOT ENABLED IT". The screen
  // would then refuse with a cause it had not established, which is the same
  // falsehood the refusal used to carry in a different sentence. It refuses
  // because it could not read the setting, and it says THAT — and the
  // approval is not offered either way, because the database admits a
  // self-review only when the organization has enabled the exception.
  const selfReview: SelfReviewException = !organization.ok
    ? { kind: 'unknown', reason: presentFailure(organization.failure).body }
    : organization.value.allow_self_review
      ? { kind: 'allowed' }
      : { kind: 'not-allowed' };

  return ok({
    publicationFailure: publication.ok ? null : publication.failure,
    draft: draft.value,
    skill,
    baseRelease: releases.value[0] ?? null,
    baseFiles: baseFiles.value,
    draftFiles: files.value,
    reviews: reviews.value,
    document,
    draftDocument: stored.ok ? stored.value : null,
    draftDocumentFailure: stored.ok ? null : stored.failure,
    diffBaseIsCurrent:
      draft.value.base_release_id === null ||
      (skill !== null && skill.current_release_id === draft.value.base_release_id),
    publication: publication.ok ? publication.value : null,
    selfReview,
  });
}

export function renderReview(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Review' }], 'Review')}
      <div class="panel">${skeletonRows(['Field', 'Value'], 5)}</div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Review' }], 'Review')}
      ${failurePanel(screen.failure, { retry: 'review:reload' })}
    `;
  }

  const data = screen.data as ReviewData;
  const viewerId = app.state.session?.userId ?? '';
  const binding = contentBinding(data.draftFiles);
  const latest = data.reviews[0] ?? null;
  // THE ORGANIZATION'S SELF-REVIEW EXCEPTION, read defensively — see
  // `selfReviewKindOf`. A payload that does not carry it is treated as an
  // unread setting, which refuses the approval and says so.
  const selfReview = selfReviewKindOf(data.selfReview);
  const verdict = decide({
    draft: data.draft,
    files: data.draftFiles,
    viewerId,
    checks: app.state.checks,
    latestApproval: latest,
    selfReview,
  });
  const changes = mayRequestChanges(data.draft, viewerId, selfReview);

  return html`
    ${docHead(
      [
        { label: 'Section', value: 'Review' },
        { label: 'Draft', value: data.draft.id.slice(0, 8) },
        { label: 'State', value: data.draft.state },
      ],
      data.skill?.title ?? 'A skill you can no longer see',
      `Reviewing a proposed revision of ${
        data.skill?.title ?? 'a skill'
      }. Nothing is published from this screen unless the publication step below says so, and that step reports what actually happened.`,
    )}

    <div class="stack">
      ${diffPanel(data)}
      <div class="split">
        <div>
          ${identityPanel(app, data)}
          ${documentPanel(data)}
        </div>
        <div>
          ${changesPanel(data, binding)}
          ${checklistPanel(app, verdict)}
          ${decisionsPanel(app, data, verdict, changes.kind !== 'blocked')}
          ${renderPublication({
            orgSlug: app.orgSlug,
            draft: data.draft,
            status: data.publication,
            busy: app.state.publishing,
            readFailure: data.publicationFailure,
          })}
          ${historyPanel(app.state.directory, data)}
        </div>
      </div>
    </div>
  `;
}

function identityPanel(app: AppShell, data: ReviewData): RawHtml {
  const base = data.baseRelease?.revision ?? 'none';
  const checkpoints = parseCheckpoints(data.draftDocument ?? data.document ?? '');
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>What is being reviewed</h2>
        ${data.draft.state === 'publish_failed'
          ? tag('Publish failed', '!', 'danger')
          : tag(data.draft.state.replace('_', ' '), 'D', 'plain')}
      </div>
      <div class="panel-body">
        <dl class="dl">
          <dt>Submitted by</dt>
          <dd>${personLabel(data.draft.author_id, app.state.directory)}</dd>
          <dt>Base revision</dt>
          <dd>${base}</dd>
          <dt>Draft version</dt>
          <dd>${data.draft.version}</dd>
          <dt>Last updated</dt>
          <dd>${formatDateTime(data.draft.updated_at)}</dd>
          <dt>Change reason</dt>
          <dd>${data.draft.change_reason ?? 'No reason was recorded with this draft.'}</dd>
          <dt>Draft files</dt>
          <dd>${data.draftFiles.length}</dd>
          <dt>Skill</dt>
          <dd>
            ${data.skill === null
              ? 'not visible to you'
              : html`<a href="/o/${app.orgSlug}/skills/${data.skill.id}">${data.skill.title}</a>`}
          </dd>
        </dl>
        ${checkpoints.length === 0
          ? html`<div class="callout">
              <span class="callout-tag">Checkpoints</span>
              <p>
                No checkpoint annotation is declared in the published document. If this procedure needs a
                human to confirm something, the document should say so.
              </p>
            </div>`
          : html`<h3>Checkpoints declared in the document</h3>
              ${checkpoints.map((note) =>
                html`<div class="checkpoint">
                  <span class="checkpoint-mark" aria-hidden="true"></span>
                  <span>
                    <span class="checkpoint-id">${note.id} · ${note.status} · line ${note.line}</span>
                    <span class="checkpoint-text"
                      >Declared in the document. Skills Manager shows where it is; it does not claim it was
                      carried out.</span
                    >
                  </span>
                </div>`,
              )}`}
      </div>
    </div>
  `;
}

/**
 * Before and after, as a reader will see them.
 *
 * The "after" pane used to be a callout saying the draft's bytes were not
 * readable. They are: `GET /v1/drafts/:draftId/files` serves them, and the
 * editor has been writing through that route. Saying otherwise on a review
 * screen told a reviewer that the thing they were being asked to approve was
 * invisible, and that was the most consequential untruth this file told.
 *
 * The rendering is `renderMarkdown`, which escapes before it formats, so a
 * draft containing raw HTML is shown as text and cannot execute in the
 * reviewer's browser. That is the "render with raw HTML disabled" rule applied
 * at the one place where it matters most.
 */
function documentPanel(data: ReviewData): RawHtml {
  const base = data.baseRelease?.revision ?? 'no published revision';
  const published = bodyOf(data.document);
  const draftBody = bodyOf(data.draftDocument);
  const draftKind = failureKindOf(data.draftDocumentFailure);
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Document</h2>
        <span class="cell-meta">rendered preview</span>
      </div>
      <div class="panel-body stack-sm">
        <h3>Before: published ${base}</h3>
        ${published === null
          ? html`<div class="callout">
              <span class="callout-tag">Unavailable</span>
              <p>
                The published document could not be fetched. This build has no current release for this
                skill, or the bundle route refused the read. Nothing below is a substitute for it.
              </p>
            </div>`
          : html`<article>${rawMarkdown(published)}</article>`}
        <h3>After: the proposed revision</h3>
        ${draftBody === null
          ? html`<div class="callout callout-attention">
              <span class="callout-tag">Unavailable</span>
              <p>
                The draft's stored ${DOCUMENT_PATH} could not be read
                ${draftKind === null ? '' : `(${draftKind})`}.
                The change set beside this is computed from the two content-hash lists, which is still
                proof of WHICH files moved — but nothing here is a substitute for reading the text.
              </p>
            </div>`
          : html`<article>${rawMarkdown(draftBody)}</article>`}
        <p class="cell-note">
          Rendered with raw HTML disabled. A draft is instructions to be read, not a web page, and a
          reviewer must not be running an author's markup in their own browser to decide on it.
        </p>
      </div>
    </div>
  `;
}

/**
 * Which lines changed, and which words.
 *
 * WHY THE WHOLE DOCUMENT AND NOT JUST THE CHANGED NEIGHBOURHOOD. A diff that
 * shows three lines of context and collapses the rest is right for a terminal
 * and wrong here: the person deciding is being asked whether the whole document
 * is correct, and a document rendered as fragments cannot be judged as a
 * document. So the screen asks for the full comparison and relies on the row
 * budget, which when it bites says so in words instead of silently showing a
 * prefix.
 *
 * WHY NOTHING HERE DEPENDS ON COLOUR. Every row states its operation three
 * ways — a `+`/`-`/space sign, the line numbers, and the word "Added",
 * "Removed" or "Unchanged" for a screen reader — before any stylesheet is
 * consulted. Word-level marks use `<ins>` and `<del>`, which are announced as
 * insertions and deletions rather than merely looking like them.
 */
function diffPanel(data: ReviewData): RawHtml {
  // `diffBaseIsCurrent !== true` rather than `=== false`: the payload is cast
  // from `unknown`, so an omitted key is `undefined`, and `undefined !== true`
  // is the conservative reading — treat "we do not know that the base is
  // current" as "it is not". A stale base shown as current invents changes.
  const staleBase = data.diffBaseIsCurrent !== true;
  const afterText = bodyOf(data.draftDocument);
  const published = bodyOf(data.document);
  // No before at all — either the base is stale, or the published body could not
  // be read. Both mean the same thing to the diff, which is that every line is
  // new; the panel below says which of the two it was.
  const beforeText = staleBase || published === null ? null : published;

  if (afterText === null) {
    return html`
      <div class="panel">
        <div class="panel-head">
          <h2>What changed</h2>
          <span class="cell-meta">line diff</span>
        </div>
        <div class="panel-body">
          <div class="callout callout-attention">
            <span class="callout-tag">No diff</span>
            <p>
              The draft's ${DOCUMENT_PATH} could not be read, so there is nothing to compare. The
              changed-files list below is computed from content hashes and still shows which files moved.
            </p>
          </div>
        </div>
      </div>
    `;
  }

  const diff = diffLines(beforeText ?? '', afterText, { context: 100_000 });
  const summary = diffSummary(diff);

  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>What changed</h2>
        <span class="cell-meta">line diff · ${DOCUMENT_PATH}</span>
      </div>
      <div class="panel-body stack-sm">
        <p class="cell-note">${summary}</p>
        ${beforeText === null
          ? html`<div class="callout callout-attention">
              <span class="callout-tag">${staleBase ? 'Base is stale' : 'No published document'}</span>
              <p>
                ${
                  staleBase
                    ? html`This draft was written against revision
                        ${data.baseRelease?.revision ?? 'an earlier release'}, but the only document this
                        deployment can serve is the CURRENT one. Diffing against it would show you changes
                        nobody made.`
                    : html`The published document could not be read, so there is nothing to compare this
                        draft against. Every line below is marked as new because nothing else was available
                        to compare with.`
                }
              </p>
            </div>`
          : ''}
        ${diff.bounded && diff.boundReason !== null
          ? html`<div class="callout callout-attention">
              <span class="callout-tag">Truncated</span>
              <p>${diff.boundReason}</p>
            </div>`
          : ''}
        ${diff.hunks.length === 0
          ? html`<div class="callout">
              <span class="callout-tag">No textual change</span>
              <p>${summary}</p>
            </div>`
          : html`<div class="diff" role="group" aria-label="Line by line comparison of ${DOCUMENT_PATH}">
              ${diff.hunks.flatMap((hunk) => [
                hunk.omittedBefore > 0
                  ? html`<div class="diff-omitted">
                      ${hunk.omittedBefore} unchanged line${hunk.omittedBefore === 1 ? '' : 's'} not shown
                    </div>`
                  : '',
                ...hunk.rows.map(diffRow),
              ])}
            </div>`}
      </div>
    </div>
  `;
}

/**
 * The counts, in words, before any colour is applied.
 *
 * `whitespaceOnly` is stated separately and explicitly, because a diff that
 * renders nothing must not be readable as "nothing changed" when the bytes are
 * not the bytes.
 */
function diffSummary(diff: LineDiff): string {
  const { added, removed, equal, whitespaceOnly } = diff.stats;
  const parts = [`${added} line${added === 1 ? '' : 's'} added`, `${removed} removed`, `${equal} unchanged`];
  if (whitespaceOnly > 0) {
    parts.push(
      `${whitespaceOnly} paragraph${whitespaceOnly === 1 ? '' : 's'} re-wrapped — the same words on different line breaks`,
    );
  }
  return `${parts.join(', ')}.`;
}

function diffRow(row: DiffRow): RawHtml {
  const label = opLabel(row.op);
  const number = row.beforeLine === null ? '' : String(row.beforeLine);
  const after = row.afterLine === null ? '' : String(row.afterLine);
  const marks =
    row.segments === null
      ? row.text
      : html`${row.segments.map((segment) => {
          const text = segment.text;
          // `ins`/`del` are the semantic elements here: a screen reader
          // announces them as an insertion and a deletion, which is the fact,
          // rather than as a colour the reader has to infer.
          if (segment.kind === 'added') return html`<ins>${text}</ins>`;
          if (segment.kind === 'removed') return html`<del>${text}</del>`;
          return text;
        })}`;
  return html`<div class="diff-line diff-${row.op}" data-pair="${row.pair === null ? '' : row.pair}">
    <span class="no" aria-hidden="true">${number}</span>
    <span class="no" aria-hidden="true">${after}</span>
    <span class="sign" aria-hidden="true">${signOf(row.op)}</span>
    <span class="diff-text">
      <span class="visually-hidden">${label} line ${row.afterLine ?? row.beforeLine ?? ''}: </span>${marks}
    </span>
  </div>`;
}

/** The rendered document. `renderMarkdown` escapes before it formats, so the result is markup. */
function rawMarkdown(source: string): RawHtml {
  return html`<div class="prose">${raw(renderMarkdown(source))}</div>`;
}

function changesPanel(
  data: ReviewData,
  binding: ContentBinding,
): RawHtml {
  const files: readonly ChangedFile[] = changedFiles(data.baseFiles, data.draftFiles);
  const meaningful = files.filter((file) => file.kind !== 'unchanged');
  const approval = data.reviews.find((row) => row.decision === 'approved') ?? null;

  return html`
    <div class="panel">
      <div class="panel-head"><h2>What this approval would bind to</h2></div>
      <div class="panel-body stack-sm">
        <p class="cell-note">
          An approval records the digest below. If any file changes afterwards the digest changes, and
          the stored approval no longer matches the content.
        </p>
        <pre class="diff"><code>${binding.display}</code></pre>
        <dl class="dl">
          <dt>Content hash</dt>
          <dd><code>${binding.hash}</code></dd>
          <dt>Algorithm</dt>
          <dd>SHA-256 over the manifest above</dd>
          <dt>Stored approval</dt>
          <dd>
            ${approval === null
              ? 'none recorded'
              : html`<code>${approval.approved_content_hash ?? 'none'}</code>
                  ${approval.approved_content_hash === binding.hash
                    ? tag('Matches', '=', 'accent')
                    : tag('Does not match', '!', 'danger')}`}
          </dd>
        </dl>
        <h3>Changed files</h3>
        ${meaningful.length === 0
          ? html`<p class="cell-note">
              No file differs from the published revision by content hash. If files were intended to
              change, the draft has not been given new content.
            </p>`
          : html`<ul class="file-list">
              ${meaningful.map(
                (file) => html`<li>
                  <span>${file.kind === 'added' ? '+' : file.kind === 'removed' ? '-' : '~'} ${file.path}</span>
                  <span>${file.beforeHash === null ? 'new' : file.afterHash === null ? 'gone' : `${file.beforeHash.slice(0, 8)} to ${file.afterHash.slice(0, 8)}`}</span>
                </li>`,
              )}
            </ul>`}
      </div>
    </div>
  `;
}

function checklistPanel(app: AppShell, verdict: ReturnType<typeof decide>): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Checklist</h2></div>
      <div class="panel-body">
        ${REVIEW_CHECKS.map((check) => {
          const checked = app.state.checks[check.id] === true;
          return html`<label class="checkline" for="check-${check.id}">
            <input
              type="checkbox"
              id="check-${check.id}"
              data-field="review.check.${check.id}"
              ${checked ? 'checked' : ''}
            />
            <span class="checkline-text">
              ${check.label}
              <span class="checkline-note">Test evidence or an explanation belongs in the change reason.</span>
            </span>
          </label>`;
        })}
        ${verdict.kind === 'blocked'
          ? html`<div class="callout callout-attention">
              <span class="callout-tag">Approval unavailable</span>
              <p>${verdict.reason}</p>
            </div>`
          : ''}
      </div>
    </div>
  `;
}

function decisionsPanel(
  app: AppShell,
  data: ReviewData,
  verdict: ReturnType<typeof decide>,
  mayRequest: boolean,
): RawHtml {
  const approved = data.draft.state === 'approved';
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Decision</h2></div>
      <div class="panel-body stack-sm">
        <label class="field-label" for="review-note">Note for the author</label>
        <textarea
          class="field"
          id="review-note"
          rows="4"
          data-field="review.note"
          placeholder="What you checked, what you would change, what the author should do next"
        >${app.state.note}</textarea>
        ${
          verdict.kind === 'approve' && verdict.selfReview
            ? html`<div class="callout">
                <span class="callout-tag">Self-review</span>
                <p>
                  You are the author of this draft, and this organization has enabled the audited
                  self-review exception. This approval will be recorded as a self-review on the
                  review row and audited, so the use of the exception is visible in the data and in
                  the trail rather than merely available.
                </p>
              </div>`
            : ''
        }
        <div class="btn-row">
          <button class="btn btn-primary" data-action="review:approve" ${verdict.kind === 'approve' ? '' : 'disabled'}>
            Approve ${verdict.kind === 'approve' ? shortHash(verdict.binding.hash) : ''}
          </button>
          <button class="btn btn-secondary" data-action="review:request-changes" ${mayRequest ? '' : 'disabled'}>
            Request changes
          </button>
        </div>
        ${approved
          ? html`<div class="callout">
              <span class="callout-tag">Approved</span>
              <p>
                This draft is approved. Publication is a separate, explicit step: the Publication panel
                below holds the control, and it says what pressing it will do and what happened
                afterwards.
              </p>
            </div>`
          : ''}
      </div>
    </div>
  `;
}


function historyPanel(directory: PeopleDirectory, data: ReviewData): RawHtml {
  if (data.reviews.length === 0) {
    return html`
      <div class="panel">
        <div class="panel-head"><h2>Decision history</h2></div>
        <div class="panel-body"><p class="cell-note">No decision has been recorded on this draft.</p></div>
      </div>
    `;
  }
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Decision history</h2></div>
      <div class="panel-body">
        ${data.reviews.map(
          (review) => html`<div class="record">
            <div class="record-head">
              <strong>${review.decision === 'approved' ? 'Approved' : 'Changes requested'}</strong>
              ${review.self_review
                ? tag('Self-review', '!', 'attention')
                : ''}
              <span class="cell-meta">${formatDateTime(review.decided_at)}</span>
            </div>
            <span class="cell-note">by ${personLabel(review.reviewer_id, directory)}</span>
            ${review.self_review
              ? html`<p class="state-meta">
                  The reviewer is this draft's author. This decision used the organization's
                  audited self-review exception, and it was recorded as such on the review and
                  in the audit trail. The exception was permitted by
                  <code>organizations.allow_self_review</code>, which is a separate fact from
                  this one.
                </p>`
              : ''}
            ${review.approved_content_hash === null
              ? ''
              : html`<p class="state-meta">bound to ${review.approved_content_hash}</p>`}
          </div>`,
        )}
      </div>
    </div>
  `;
}
